import {
	Component,
	OnInit,
	Input,
	Output,
	EventEmitter,
	SimpleChanges,
	OnChanges,
	OnDestroy
} from '@angular/core';
import { InvoicesDataProvider } from '@services/invoice/invoices-data-service/invoices.data.service';
import { MessageService } from '@services/helpers/message-service/message.service';
import { Invoice, Payment } from '@web-components/invoices/invoice.model';
import { ValidationProvider } from '@services/helpers/validation.service';
import { Subject } from 'rxjs/Subject';
import { finalize, takeUntil } from 'rxjs/operators';
import { InvoicePayment } from '../../../../../shared/models/models';

@Component({
	selector: 'invoice-view',
	template: require('./invoice-view.html'),
	styles: [require('./invoice-view.scss')]
})
export class InvoiceViewComponent implements OnInit, OnChanges, OnDestroy {
	@Input('invoiceId') invoiceId: number;
	@Input() sendReceiptSpinner: boolean;
	@Output() sendMail: EventEmitter<any> = new EventEmitter();
	@Output() getContactEmail: EventEmitter<string> = new EventEmitter();
	invoice: Invoice;
	isLeadView: boolean;
	userCurrencySymbol: string;
	selectedPayment: InvoicePayment;
	paidOn: Date;
	spinners;

	private unsubscribe: Subject<any> = new Subject();

	constructor(
		private invoiceDataService: InvoicesDataProvider,
		private messageService: MessageService,
		private validationService: ValidationProvider
	) {}

	public ngOnInit(): void {
		this.configureCommunicationAdapter();
		this.getInvoice();
	}

	public ngOnChanges(change: SimpleChanges): void {
		// We need to track infinite spinner variable from old Angular. Could be removed after upgrading SendReceipt modal.
		if (
			!change.sendReceiptSpinner.isFirstChange() &&
			this.selectedPayment &&
			this.spinners.sendReceiptSpinner[this.selectedPayment.id]
		) {
			this.spinners.sendReceiptSpinner[this.selectedPayment.id] =
				change.sendReceiptSpinner.currentValue;
		}
	}

	public ngOnDestroy(): void {
		this.unsubscribe.next();
		this.unsubscribe.complete();
	}

	private getInvoice() {
		this.invoiceDataService
			.getInvoiceV1(this.invoiceId)
			.pipe(takeUntil(this.unsubscribe))
			.subscribe(
				data => this.successGetInvoice(data),
				(error): void => this.messageService.showDefaultErrorMessage(error)
			);
	}

	private successGetInvoice(invoice): void {
		this.invoice = invoice;
		this.isLeadView = invoice.job.inLead;
		this.userCurrencySymbol = invoice.summary.totalWithCurrency.currencySymbol;
		this.getContactEmail.emit(invoice.job.primaryContact.email);
		this.prepareSpinners();
	}

	public setPaid(payment: InvoicePayment): void {
		this.selectPayment(payment);
		if (
			payment.errorMessage ===
			"StudioNinja doesn't support transactions with Credit Notes from QuickBooks"
		) {
			this.messageService.showToaster('e_14', 'qb');
		} else if (
			payment.errorMessage ===
			'One of the list elements assigned to this transaction has been deleted'
		) {
			this.messageService.showToaster('e_11', 'qb');
		} else {
			this.selectedPayment = payment;
			const setPaidModal = $('#markAsPayedModal');

			setPaidModal.modal('show').on('shown.bs.modal', () => {
				this.configurePreventCloseModal(setPaidModal);
			});
		}
	}

	private configurePreventCloseModal(modal): void {
		const input = modal.find('input');
		modal.find('input.ws-number').prop('autocomplete', 'off');

		this.validationService.isModalClosePrevented(input);
	}

	public onRecordPaymentClose(ev: boolean): void {
		if (ev) {
			this.getInvoice();
		}
	}

	public setUnpaid(payment: InvoicePayment): void {
		this.selectPayment(payment);
		this.spinners.markUnpaidSpinner[payment.id] = true;
		if (this.invoice.qbStatus === 'DISCONNECTED_INVOICE_ALTERED') {
			this.messageService.showToaster('e_12', 'qb');
		} else {
			this.invoiceDataService
				.setUnpaid(payment.id)
				.pipe(
					takeUntil(this.unsubscribe),
					finalize(() => this.discardSpinners())
				)
				.subscribe(
					result => this.successSetUnpaid(result),
					error => this.errorSetUnpaid
				);
		}
	}

	private successSetUnpaid(resp): void {
		if (resp && resp.data && resp.data.errorMessage) {
			this.messageService.showDefaultErrorMessage(resp);
		} else {
			this.messageService.showToaster('s_3', 'payments');
		}
		this.getInvoice();
	}

	private errorSetUnpaid({ status, data }) {
		const errorMessage = data.errorMessage;
		let errorCode, errorPlace, value;

		if (status >= 400 && status < 500 && errorMessage) {
			if (
				errorMessage.includes(
					'This payment cannot be marked as unpaid because it was overpaid in Xero.'
				)
			) {
				errorCode = 'e_11';
				errorPlace = 'xero';
			} else {
				errorCode = 'e_2';
				errorPlace = 'general';
				value = errorMessage;
			}
		} else {
			errorCode = 'e_0';
			errorPlace = 'general';
		}
		this.messageService.showToaster(errorCode, errorPlace, value);
	}

	public showWriteOff(payment: InvoicePayment): void {
		this.selectPayment(payment);
		setTimeout(() => $('#writeOffModal').modal());
	}

	public writeOff(): void {
		this.getInvoice();
	}

	public undoOff(payment: InvoicePayment): void {
		this.selectPayment(payment);
		this.spinners.undoWriteOffSpinner[payment.id] = true;
		this.invoiceDataService
			.undoWriteOff(payment.id)
			.pipe(
				takeUntil(this.unsubscribe),
				finalize(() => this.discardSpinners())
			)
			.subscribe(
				() => this.getInvoice(),
				error => this.messageService.showDefaultErrorMessage(error)
			);
	}

	public payOnline(): void {
		const newWindow = window.open('', '_blank');

		this.invoiceDataService
			.getInvoicePreviewURL(this.invoiceId)
			.pipe(takeUntil(this.unsubscribe))
			.subscribe(result => {
				const clientLink = result.link;
				const base = window.location.origin;
				newWindow.location.href = `${base}/clients/invoice/pay/${clientLink}/${
					this.invoiceId
				}`;
			});
	}

	public sendReceipt(sendData): void {
		if (sendData.isReceipt) {
			this.spinners.sendReceiptSpinner[sendData.data.id] = true;
			this.selectPayment(<InvoicePayment>sendData.data);
		}

		this.sendMail.emit(sendData);
	}

	private prepareSpinners(): void {
		this.spinners = {
			sendReceiptSpinner: {},
			markUnpaidSpinner: {},
			undoWriteOffSpinner: {}
		};

		this.invoice.payments.map(e => {
			this.spinners.sendReceiptSpinner[e.id] = this.spinners.markUnpaidSpinner[
				e.id
			] = this.spinners.undoWriteOffSpinner[e.id] = false;
		});
	}

	private selectPayment(payment: InvoicePayment): void {
		this.selectedPayment = payment;
	}

	private discardSpinners(): void {
		this.spinners.sendReceiptSpinner[
			this.selectedPayment.id
		] = this.spinners.markUnpaidSpinner[
			this.selectedPayment.id
		] = this.spinners.undoWriteOffSpinner[this.selectedPayment.id] = false;
	}

	private configureCommunicationAdapter(): void {
		window.snCommunicationAdapter.registerMethod(
			'getInvoice',
			this.getInvoice.bind(this)
		);
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=kkml25e9zf!./web/modern/components/invoices/invoice-view/invoice-view.ts